import { AlertDialog, useDialog as useDaxDialog } from '@global-dax-ad-platform/dax-components';
import { Button } from '@global-engineering-shared/gweb-material-ui';
import { Typography } from '@global-engineering-shared/gweb-material-ui';
import { Fragment, useEffect, useState } from 'react';

import { useDialog } from 'context/DialogProvider/DialogProvider';
import { useFilters } from 'context/FilterProvider/FilterProvider';
import { usePaginatedItemsContext } from 'context/PaginatedItems/context/PaginatedItemsProvider';
import { useSearchParamsProvider } from 'context/SearchParamsProvider/SearchParamsProvider';
import { useSelectedEntitiesContext } from 'context/SelectedEntities/SelectedEntitiesProvider';
import useStyles from 'features/Entities/components/EntitiesPageActionButtons/EntitiesPageActionButtons.styles';
import { actions } from 'features/Entities/components/EntitiesPageActionButtons/utils/actionButtonsConfig';
import {
  getActionConfig,
  getConfirmDialogConfig,
} from 'features/Entities/components/EntitiesPageActionButtons/utils/getActionConfig';
import EntitiesPageActionDialog from 'features/Entities/components/EntitiesPageActionDialog';
import EntitiesPageResultDialog from 'features/Entities/components/EntitiesPageResultDialog';
import { IActionResponse } from 'features/Entities/types/ActionResponse';
import useFetchData from 'hooks/useFetchData';
import { EntityActions } from 'utils/actions';
import {
  ENTITIES_PAGE_ACTION_DIALOG_ID,
  ENTITIES_PAGE_CONFIRMATION_DIALOG_ID,
  ENTITIES_PAGE_RESULT_DIALOG_ID,
  SEARCH_TERM,
  ENTITY_ACTIONS_API_URL,
} from 'utils/defaults';
import { toFiltersSearchParams } from 'utils/searchParamsConverter';
import { LiveStatus } from 'utils/statuses';

const EntitiesPageActionButtons = () => {
  const classes = useStyles();
  const { patchData, getData } = useFetchData();
  const {
    areSelectedEntitiesInSameStatus,
    selectedEntityIds,
    numberOfSelectedEntitiesInStatus,
    resetSelectedEntities,
  } = useSelectedEntitiesContext();
  const { searchParams } = useSearchParamsProvider();
  const { openDialog } = useDialog();
  const { openDialog: openDaxDialog } = useDaxDialog();
  const { filters } = useFilters();
  const { totalElements } = usePaginatedItemsContext();
  const [bulkActions, setBulkActions] = useState<EntityActions[]>([]);
  const searchTerm = searchParams.get(SEARCH_TERM);
  const selectedIds = selectedEntityIds();

  const initialActionResponse: IActionResponse = {
    totalItemsActioned: 0,
    spaceIdsWithDraft: [],
    spaceIdsWithoutDraft: [],
  };

  const [actionResponse, setActionResponse] = useState<IActionResponse>(initialActionResponse);

  const getAvailableActions = async () => {
    const params = toFiltersSearchParams(filters);
    await getData(`${ENTITY_ACTIONS_API_URL}?${params}`, (response: EntityActions[]) => {
      setBulkActions(response);
    });
  };

  useEffect(() => {
    resetSelectedEntities();
    getAvailableActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters]);

  const handleAction = async (action: EntityActions, status: LiveStatus) => {
    if (!areSelectedEntitiesInSameStatus(status)) {
      openDialog(ENTITIES_PAGE_ACTION_DIALOG_ID + action);
      return;
    }
    openDaxDialog(ENTITIES_PAGE_CONFIRMATION_DIALOG_ID + action);
  };

  const handleConfirm = async (url: string, params: URLSearchParams) => {
    await patchData<Record<string, never>, IActionResponse>(`${url}?${params}`, {}, (data) => {
      setActionResponse(data);
      openDialog(ENTITIES_PAGE_RESULT_DIALOG_ID);
    });
  };
  const hasActiveFilters = Object.values(filters).some((values) => values.length > 0);
  const isBulk =
    totalElements > 0 &&
    hasActiveFilters &&
    bulkActions.length > 0 &&
    !selectedIds.length &&
    !searchTerm;

  return (
    <div className={classes.container}>
      <EntitiesPageResultDialog actionResponse={actionResponse} />
      {actions.map(({ action, status, url, color }) => {
        const { label, count, shouldDisplay, entityPhraser, capitalizedEntityPhraser } =
          getActionConfig({
            action,
            status,
            isBulk,
            totalElements,
            numberOfSelectedEntitiesInStatus,
            bulkActions,
          });

        const { confirmDialogId, confirmParams } = getConfirmDialogConfig({
          action,
          isBulk,
          filters,
          selectedIds,
        });

        if (!shouldDisplay) return null;

        return (
          <Fragment key={action}>
            <AlertDialog
              dialogId={confirmDialogId}
              content={{
                title: `${label} ${capitalizedEntityPhraser}`,
                main: (
                  <Typography color="textPrimary" variant="body2">
                    Click continue to <strong>{label.toLowerCase()}</strong> {count} {entityPhraser}
                    .
                  </Typography>
                ),
              }}
              action={{
                label: 'Continue',
                onClick: () => handleConfirm(url, confirmParams),
              }}
            />
            {!isBulk && <EntitiesPageActionDialog action={action} status={status} />}
            <Button
              onClick={() =>
                isBulk ? openDaxDialog(confirmDialogId) : handleAction(action, status)
              }
              variant="outlined"
              color={color || 'primary'}
              className={classes.root}
              fontWeight={500}
            >
              {label}
              {isBulk ? ' All' : ''}
            </Button>
          </Fragment>
        );
      })}
    </div>
  );
};

export default EntitiesPageActionButtons;
